import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { setCurrentTenant, clearCurrentTenant } from '../services/tenant/tenantAccessor';

/**
 * Tenant information resolved from authentication or subdomain.
 */
export interface TenantInfo {
  tenantIdentifier: string | null;
  azureTenantId: string | null;
  tenantName: string | null;
  isResolved: boolean;
  isCatalogContext: boolean;
}

interface TenantContextValue extends TenantInfo {
  setTenant: (identifier: string, azureTenantId?: string, name?: string) => void;
  clearTenant: () => void;
  setCatalogContext: () => void;
  getTenantHeader: () => string | null;
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

/**
 * Extracts subdomain from current hostname.
 * Returns null for localhost, IP addresses, or if no subdomain exists.
 */
function extractSubdomain(hostname: string): string | null {
  const host = hostname.split(':')[0];

  if (host === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(host)) {
    return null;
  }

  const parts = host.split('.');

  if (parts.length >= 3) {
    const subdomain = parts[0].toLowerCase();
    const skipSubdomains = ['www', 'api', 'admin', 'portal', 'app'];
    if (!skipSubdomains.includes(subdomain)) {
      return subdomain;
    }
  }

  return null;
}

interface TenantProviderProps {
  children: React.ReactNode;
}

/**
 * Provides tenant context for multi-tenant database isolation.
 * Resolves tenant from:
 * 1. Subdomain (e.g., acme.leftshield.io)
 * 2. Azure AD tenant ID from authentication token
 */
export function TenantProvider({ children }: TenantProviderProps) {
  const { accounts } = useMsal();

  const [tenantInfo, setTenantInfo] = useState<TenantInfo>({
    tenantIdentifier: null,
    azureTenantId: null,
    tenantName: null,
    isResolved: false,
    isCatalogContext: false,
  });

  // Resolve tenant from subdomain or auth on mount
  useEffect(() => {
    const subdomain = extractSubdomain(window.location.hostname);
    if (subdomain) {
      setTenantInfo({
        tenantIdentifier: subdomain,
        azureTenantId: null,
        tenantName: null,
        isResolved: true,
        isCatalogContext: false,
      });
      setCurrentTenant(subdomain, null, false);
      return;
    }

    const account = accounts[0];
    if (account?.tenantId) {
      setTenantInfo({
        tenantIdentifier: null,
        azureTenantId: account.tenantId,
        tenantName: account.name || null,
        isResolved: true,
        isCatalogContext: false,
      });
      setCurrentTenant(null, account.tenantId, false);
      return;
    }

    const isAdminRoute = window.location.pathname.startsWith('/admin');
    if (isAdminRoute) {
      setTenantInfo({
        tenantIdentifier: null,
        azureTenantId: null,
        tenantName: null,
        isResolved: true,
        isCatalogContext: true,
      });
      setCurrentTenant(null, null, true);
    }
  }, [accounts]);

  // Sync tenant state with accessor whenever it changes
  useEffect(() => {
    if (tenantInfo.isResolved) {
      setCurrentTenant(
        tenantInfo.tenantIdentifier,
        tenantInfo.azureTenantId,
        tenantInfo.isCatalogContext
      );
    }
  }, [tenantInfo]);

  const setTenant = useCallback((identifier: string, azureTenantId?: string, name?: string) => {
    setTenantInfo({
      tenantIdentifier: identifier,
      azureTenantId: azureTenantId || null,
      tenantName: name || null,
      isResolved: true,
      isCatalogContext: false,
    });
  }, []);

  const clearTenant = useCallback(() => {
    setTenantInfo({
      tenantIdentifier: null,
      azureTenantId: null,
      tenantName: null,
      isResolved: false,
      isCatalogContext: false,
    });
    clearCurrentTenant();
  }, []);

  const setCatalogContext = useCallback(() => {
    setTenantInfo({
      tenantIdentifier: null,
      azureTenantId: null,
      tenantName: null,
      isResolved: true,
      isCatalogContext: true,
    });
  }, []);

  const getTenantHeader = useCallback((): string | null => {
    if (tenantInfo.isCatalogContext) {
      return null;
    }
    return tenantInfo.tenantIdentifier || tenantInfo.azureTenantId || null;
  }, [tenantInfo]);

  const value = useMemo<TenantContextValue>(() => ({
    ...tenantInfo,
    setTenant,
    clearTenant,
    setCatalogContext,
    getTenantHeader,
  }), [tenantInfo, setTenant, clearTenant, setCatalogContext, getTenantHeader]);

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

/**
 * Hook to access the current tenant context.
 */
export function useTenant(): TenantContextValue {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}

/**
 * Hook to get just the tenant identifier for API calls.
 */
export function useTenantIdentifier(): string | null {
  const { getTenantHeader } = useTenant();
  return getTenantHeader();
}
